/**
 * Corrosion Gradient Definitions
 *
 * Provides custom color gradients for visualizing pipeline corrosion severity.
 * Gradients map distance deviations to colors using Potree's gradient system.
 *
 * Color Mapping (aligned with enums.js categories):
 * - Green: 0-2mm (Cat A - Light scale / Normal)
 * - Yellow: 2-4mm (Cat B - Moderate scale / Minor)
 * - Orange: 4-6mm (Cat C - Heavy scale / Moderate)
 * - Red: 6mm+ (Cat D - Severe scale / Critical)
 */

import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';

const THREE = window.THREE;

/**
 * Default corrosion gradient
 * Maps deviation distance to color for intuitive visualization
 */
export const CORROSION_GRADIENT = [
  // Stage 0 (Position 0.0) - Cat A: 0-2mm (Green)
  [0.00, new THREE.Color(0.000, 1.000, 0.000)], // Pure green

  // Stage 1 (Position 0.33) - Cat B: 2-4mm (Yellow)
  [0.33, new THREE.Color(1.000, 1.000, 0.000)], // Pure yellow

  // Stage 2 (Position 0.66) - Cat C: 4-6mm (Orange)
  [0.66, new THREE.Color(1.000, 0.647, 0.000)], // Pure orange (#FFA500)

  // Stage 3 (Position 1.0) - Cat D: 6mm+ (Red)
  [1.00, new THREE.Color(1.000, 0.000, 0.000)]  // Pure red
];

/**
 * High contrast gradient for better visibility in bright conditions
 */
export const CORROSION_GRADIENT_HIGH_CONTRAST = [
  [0.00, new THREE.Color(0.000, 0.800, 0.000)], // Dark green - Stage 0
  [0.33, new THREE.Color(1.000, 0.900, 0.000)], // Bright yellow - Stage 1
  [0.66, new THREE.Color(1.000, 0.400, 0.000)], // Bright orange - Stage 2
  [1.00, new THREE.Color(0.800, 0.000, 0.000)]  // Dark red - Stage 3
];

/**
 * Thermal-style gradient (blue to red)
 * Maps cold (normal) to hot (critical)
 */
export const CORROSION_GRADIENT_THERMAL = [
  [0.00, new THREE.Color(0.000, 0.000, 1.000)], // Blue (cold/normal) - Stage 0
  [0.33, new THREE.Color(0.000, 1.000, 0.700)], // Cyan/Teal - Stage 1
  [0.66, new THREE.Color(1.000, 0.650, 0.000)], // Orange - Stage 2
  [1.00, new THREE.Color(1.000, 0.000, 0.000)]  // Red (hot/critical) - Stage 3
];

/**
 * Monochrome gradient for colorblind accessibility
 * Light to dark grayscale
 */
export const CORROSION_GRADIENT_MONOCHROME = [
  [0.00, new THREE.Color(1.000, 1.000, 1.000)], // White (normal) - Stage 0
  [0.33, new THREE.Color(0.667, 0.667, 0.667)], // Light gray - Stage 1
  [0.66, new THREE.Color(0.333, 0.333, 0.333)], // Dark gray - Stage 2
  [1.00, new THREE.Color(0.000, 0.000, 0.000)]  // Black (critical) - Stage 3
];

/**
 * Get gradient by style name
 *
 * @param {string} style - Gradient style name
 * @returns {Array} Gradient array
 */
export function getGradientByStyle(style = 'default') {
  const gradients = {
    'default': CORROSION_GRADIENT,
    'high-contrast': CORROSION_GRADIENT_HIGH_CONTRAST,
    'thermal': CORROSION_GRADIENT_THERMAL,
    'monochrome': CORROSION_GRADIENT_MONOCHROME
  };

  return gradients[style] || CORROSION_GRADIENT;
}

/**
 * Get color for a specific deviation value
 * Useful for legends and UI elements
 *
 * @param {number} deviation - Deviation in mm
 * @param {number} maxDeviation - Maximum deviation (default: 6mm)
 * @param {string} style - Gradient style
 * @returns {THREE.Color} Interpolated color
 */
export function getColorForDeviation(deviation, maxDeviation = 6.0, style = 'default') {
  const gradient = getGradientByStyle(style);
  const normalized = Math.max(0, Math.min(1, deviation / maxDeviation));

  // Find the two gradient stops to interpolate between
  let lowerStop = gradient[0];
  let upperStop = gradient[gradient.length - 1];

  for (let i = 0; i < gradient.length - 1; i++) {
    if (normalized >= gradient[i][0] && normalized <= gradient[i + 1][0]) {
      lowerStop = gradient[i];
      upperStop = gradient[i + 1];
      break;
    }
  }

  // Linear interpolation between stops
  const [lowerPos, lowerColor] = lowerStop;
  const [upperPos, upperColor] = upperStop;

  if (lowerPos === upperPos) {
    return lowerColor.clone();
  }

  const t = (normalized - lowerPos) / (upperPos - lowerPos);
  const interpolatedColor = lowerColor.clone().lerp(upperColor, t);

  return interpolatedColor;
}

/**
 * Get severity level for a deviation value
 *
 * @param {number} deviation - Deviation in mm
 * @returns {Object} Severity info {level, label, color, description}
 */
export function getSeverityLevel(deviation) {
  if (deviation < 2.0) {
    return {
      level: 0,
      label: 'Normal',
      color: '#00FF00',
      description: 'Normal surface condition',
      icon: '✅'
    };
  } else if (deviation < 4.0) {
    return {
      level: 1,
      label: 'Minor Wear',
      color: '#FFFF00',
      description: 'Minor wear detected - monitor',
      icon: '⚠️'
    };
  } else if (deviation < 6.0) {
    return {
      level: 2,
      label: 'Moderate Corrosion',
      color: '#FF8800',
      description: 'Moderate corrosion - action needed',
      icon: '⚠️'
    };
  } else {
    return {
      level: 3,
      label: 'Critical Corrosion',
      color: '#FF0000',
      description: 'Critical corrosion - immediate action required',
      icon: '🚨'
    };
  }
}

/**
 * Generate legend data for UI display
 *
 * @param {string} style - Gradient style
 * @returns {Array} Legend items with color, label, and range
 */
export function generateLegendData(style = 'default') {
  const gradient = getGradientByStyle(style);

  return [
    {
      color: getColorForDeviation(1.0, 6.0, style).getStyle(),
      label: 'Cat A',
      range: '0-2mm',
      severity: 'Light scale'
    },
    {
      color: getColorForDeviation(3.0, 6.0, style).getStyle(),
      label: 'Cat B',
      range: '2-4mm',
      severity: 'Moderate scale'
    },
    {
      color: getColorForDeviation(5.0, 6.0, style).getStyle(),
      label: 'Cat C',
      range: '4-6mm',
      severity: 'Heavy scale / Flaking'
    },
    {
      color: getColorForDeviation(7.0, 6.0, style).getStyle(),
      label: 'Cat D',
      range: '6mm+',
      severity: 'Severe scale / Pitting'
    }
  ];
}

/**
 * Create a custom gradient based on user-defined thresholds
 *
 * @param {Object} thresholds - User-defined thresholds in meters
 * @param {number} thresholds.normalToMinor - Threshold between normal and minor wear (m)
 * @param {number} thresholds.minorToModerate - Threshold between minor wear and moderate corrosion (m)
 * @param {number} thresholds.moderateToCritical - Threshold between moderate and critical corrosion (m)
 * @param {number} thresholds.maxDisplay - Maximum display range (m)
 * @returns {Array} Custom gradient array compatible with Potree
 */
export function createCustomGradient(thresholds) {
  const {
    normalToMinor = 0.002,    // 2mm default - Cat A -> Cat B
    minorToModerate = 0.004,  // 4mm default - Cat B -> Cat C
    moderateToCritical = 0.006, // 6mm default - Cat C -> Cat D
    maxDisplay = 0.015        // 15mm default
  } = thresholds;

  // Validate thresholds
  if (normalToMinor >= minorToModerate || minorToModerate >= moderateToCritical) {
    debugWarn('[Corrosion Gradient] Invalid thresholds, using defaults');
    return CORROSION_GRADIENT;
  }

  // Calculate normalized positions (0.0 to 1.0)
  const pos1 = normalToMinor / maxDisplay;
  const pos2 = minorToModerate / maxDisplay;
  const pos3 = moderateToCritical / maxDisplay;

  // Create gradient with 4 stages aligned to severity levels
  const customGradient = [
    // Stage 0: Normal (0 to normalToMinor) - Green
    [0.00, new THREE.Color(0.000, 1.000, 0.000)], // Pure green

    // Stage 1: Minor Wear (normalToMinor to minorToModerate) - Yellow
    [pos1, new THREE.Color(1.000, 1.000, 0.000)], // Pure yellow

    // Stage 2: Moderate (minorToModerate to moderateToCritical) - Orange
    [pos2, new THREE.Color(1.000, 0.647, 0.000)], // Pure orange

    // Stage 3: Critical (moderateToCritical to maxDisplay) - Red
    [pos3, new THREE.Color(1.000, 0.000, 0.000)]  // Pure red
  ];

  debugLog('[Corrosion Gradient] Custom gradient created:', {
    normalToMinor: `${normalToMinor * 1000}mm (pos: ${pos1.toFixed(3)})`,
    minorToModerate: `${minorToModerate * 1000}mm (pos: ${pos2.toFixed(3)})`,
    moderateToCritical: `${moderateToCritical * 1000}mm (pos: ${pos3.toFixed(3)})`,
    maxDisplay: `${maxDisplay * 1000}mm`,
    gradientStops: customGradient.length
  });

  return customGradient;
}

/**
 * Validate threshold values
 *
 * @param {Object} thresholds - Thresholds to validate
 * @returns {Object} Validation result { valid: boolean, errors: string[] }
 */
export function validateThresholds(thresholds) {
  const errors = [];

  if (!thresholds) {
    return { valid: false, errors: ['Thresholds object is required'] };
  }

  const { normalToMinor, minorToModerate, moderateToCritical, maxDisplay } = thresholds;

  // Check for required fields
  if (normalToMinor === undefined) errors.push('normalToMinor is required');
  if (minorToModerate === undefined) errors.push('minorToModerate is required');
  if (moderateToCritical === undefined) errors.push('moderateToCritical is required');
  if (maxDisplay === undefined) errors.push('maxDisplay is required');

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  // Check for proper ordering
  if (normalToMinor >= minorToModerate) {
    errors.push('normalToMinor must be less than minorToModerate');
  }
  if (minorToModerate >= moderateToCritical) {
    errors.push('minorToModerate must be less than moderateToCritical');
  }
  if (moderateToCritical >= maxDisplay) {
    errors.push('moderateToCritical must be less than maxDisplay');
  }

  // Check for reasonable ranges
  if (normalToMinor <= 0) {
    errors.push('normalToMinor must be greater than 0');
  }
  if (maxDisplay > 0.1) { // 100mm seems excessive
    errors.push('maxDisplay should not exceed 100mm (0.1m)');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Get default threshold values
 *
 * @returns {Object} Default thresholds in meters
 */
export function getDefaultThresholds() {
  return {
    normalToMinor: 0.002,    // 2mm - Cat A -> Cat B
    minorToModerate: 0.004,  // 4mm - Cat B -> Cat C
    moderateToCritical: 0.006, // 6mm - Cat C -> Cat D
    maxDisplay: 0.015        // 15mm
  };
}

/**
 * Export all gradients and utilities
 */
export default {
  CORROSION_GRADIENT,
  CORROSION_GRADIENT_HIGH_CONTRAST,
  CORROSION_GRADIENT_THERMAL,
  CORROSION_GRADIENT_MONOCHROME,
  getGradientByStyle,
  getColorForDeviation,
  getSeverityLevel,
  generateLegendData,
  createCustomGradient,
  validateThresholds,
  getDefaultThresholds
};
